import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { InceptionTypeInputEnum } from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import useSessionUser from '@/modules/access/hooks/useSessionUser'
import { useCreateProduct } from '@/modules/admin-center/hooks/useCreateProduct'
import { useCreateOrUpdatePartyProduct } from '@/modules/products/hooks/useCreateOrUpdatePartyProduct'
import { useCreateRequisitionLine } from '@/modules/requisitions/hooks/useCreateRequisitionLine'
import { generateShopPath } from '@/modules/requisitions/utils/generateShopPath'
import { CreateProductFormInputs } from '@/modules/shared/components/create-product-modal/CreateProductFormSchema'
import { displayToast } from '@/modules/shared/components/toast/displayToast'
import { useAlert } from '@/modules/shared/hooks/useAlert'

export interface UseCreateAndAddProductProps {
  isUtility?: boolean
  quickAdd?: boolean
  refetch?: () => void
  onCloseModal?: () => void
  id?: string
}

export function useCreateAndAddProductRequisition({ quickAdd, onCloseModal, id }: UseCreateAndAddProductProps) {
  const { currentOrganisation } = useSessionUser()
  const [createProduct, { loading: createProductLoading }] = useCreateProduct()
  const [createRequisitionLine, { loading: createRequisitionLineLoading }] = useCreateRequisitionLine(null)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [createOrUpdatePartlyProduct] = useCreateOrUpdatePartyProduct()
  const { alertDialog } = useAlert()

  const onCreateAndAddProductRequisition = (input: CreateProductFormInputs, isAPurchaser?: boolean) => {
    const {
      categoryId,
      brand,
      itemDescription,
      unitPrice,
      taxPercentage,
      quantity,
      itemMeasure,
      itemSize,
      itemPackName,
      itemSellPackName,
      itemSellQuantity,
      supplierId,
      code,
      externalId,
    } = input
    createProduct({
      variables: {
        input: {
          categoryId,
          brand,
          itemDescription,
          locale: 'en',
          itemMeasure,
          itemSize,
          itemPackName: itemPackName === 'null' ? null : itemPackName,
          itemSellPackName,
          itemSellQuantity,
          externalId,
          inceptionType: InceptionTypeInputEnum.Manual,
          ownerCode: isAPurchaser ? code : undefined,
        },
      },
      onCompleted: async (data) => {
        const createdProductId = Number(data.createProduct.product?.id)
        if (code) {
          await createOrUpdatePartlyProduct({
            variables: {
              input: {
                productId: createdProductId,
                organisationId: Number(currentOrganisation?.id),
                code: code,
              },
            },
          })
        }
        createRequisitionLine({
          variables: {
            input: {
              supplierId: Number(supplierId),
              productId: createdProductId,
              requisitionId: Number(id),
              unitPrice: Number(unitPrice),
              taxPercentage: Number(taxPercentage),
              quantity: Number(quantity),
            },
          },
          onCompleted(newLineData) {
            if (quickAdd) {
              onCloseModal && onCloseModal()
              displayToast({ title: t('general.productAdded', 'Product Added') })
            } else {
              navigate(
                generateShopPath(
                  newLineData.createRequisitionLine.requisitionLine?.requisition?.id,
                  newLineData.createRequisitionLine.requisitionLine?.requisition?.advanced
                )
              )
            }
          },
        })
      },
      onError() {
        alertDialog({ type: 'error' })
      },
    })
  }

  return { onCreateAndAddProductRequisition, loading: createProductLoading || createRequisitionLineLoading }
}
